'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @primitive Dialog
 * ROLE: dialog | ARIA: aria-modal=true, aria-labelledby, aria-describedby
 * KEYBOARD: ESC closes; Tab cycles inside; Shift+Tab reverse
 * FOCUS: enters first focusable on open; restores to trigger on close
 * LIVE-REGION: none (dialog title announces on open)
 * PRM: animation disabled; opacity instant — see Pillar 12
 * FORCED-COLORS: backdrop = Canvas; content border = ButtonText
 * RTL: backdrop + scroll-lock direction-agnostic
 * RSC: CLIENT
 * WAI-APG: https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/
 * SUBSTRATE: @radix-ui/react-dialog
 *
 * COMPOSITION: Dialog is a band container — header (rendered by Dialog), then
 * any number of `<DialogSection>` bodies, then `<DialogFooter>`. Loose children
 * are auto-wrapped in a default padded `<DialogSection>` so simple callers can
 * still pass JSX directly. Lists/full-bleed content opt into
 * `<DialogSection bleed scrollable>` to span edge-to-edge and scroll
 * independently of the header/footer.
 *
 * POSITION: `position='center'` (default) renders the classic centered modal.
 * `position='side'` anchors to the inline-end edge as a full-height side panel
 * that slides in from the right. Width is governed by `size` in both modes.
 */
import * as RadixDialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { Children, Fragment, isValidElement, } from 'react';
import { VisuallyHidden } from '../../VisuallyHidden/VisuallyHidden';
import styles from './Dialog.module.css';
// Counts renderable child nodes, descending through empty Fragments + arrays
// so `<></>`, `null`, `false`, and `{cond ? <X/> : null}` (all false) all
// resolve to 0. A DialogSection with zero renderable children must not render
// its band: the `.section` chrome (padding + top hairline) would otherwise
// draw an empty horizontal band between the header/description and the next
// band — the recurring "empty modal body" bug. Guarding here fixes it for
// every caller (explicit `<DialogSection>` bodies whose contents are all
// conditional), not one call site at a time.
function countRenderableChildren(node) {
    if (node === null || node === undefined || typeof node === 'boolean')
        return 0;
    if (Array.isArray(node)) {
        let n = 0;
        for (const item of node)
            n += countRenderableChildren(item);
        return n;
    }
    if (isValidElement(node) && node.type === Fragment) {
        const props = node.props;
        return countRenderableChildren(props.children);
    }
    return 1;
}
export function DialogSection({ bleed, scrollable, divider, gap, children }) {
    // Empty body → no band (see countRenderableChildren). Prevents the empty
    // horizontal band when every child is a falsy conditional.
    if (countRenderableChildren(children) === 0)
        return null;
    const classes = [
        styles.section,
        bleed ? styles['section--bleed'] : null,
        scrollable ? styles['section--scrollable'] : null,
        divider ? styles['section--divider'] : null,
    ]
        .filter((c) => c !== null && c !== undefined)
        .join(' ');
    const style = gap
        ? { display: 'flex', flexDirection: 'column', gap: `var(--space-${gap})` }
        : undefined;
    return (_jsx("div", { className: classes, style: style, children: children }));
}
const FOOTER_JUSTIFY_CLASS = {
    'flex-end': styles['footer-end'],
    'space-between': styles['footer-between'],
    'flex-start': styles['footer-start'],
    center: styles['footer-center'],
};
export function DialogFooter({ children, justify = 'flex-end', className }) {
    return (_jsx("div", { className: `${styles.footer} ${FOOTER_JUSTIFY_CLASS[justify]}${className ? ` ${className}` : ''}`, children: children }));
}
// Group raw children into bands. Anything that isn't already a `DialogSection`
// or `DialogFooter` is collected into a single implicit padded section so most
// existing callers don't need to wrap their bodies explicitly. Callers that
// need bleed/scrollable behavior opt in by passing `<DialogSection>` directly.
function intoBands(children) {
    const flat = Children.toArray(children);
    const out = [];
    let pending = [];
    const flushPending = () => {
        if (pending.length === 0)
            return;
        // Default gap matches the old `.content`'s flex-column gap so existing
        // dialogs that pass loose siblings keep their vertical rhythm. Callers
        // who want a different cadence pass `<DialogSection>` explicitly.
        out.push(_jsx(DialogSection, { gap: "lg", children: pending }, `auto-${out.length}`));
        pending = [];
    };
    for (const child of flat) {
        if (isValidElement(child) &&
            (child.type === DialogSection ||
                child.type === DialogFooter)) {
            flushPending();
            out.push(child);
        }
        else {
            pending.push(child);
        }
    }
    flushPending();
    return out.length === 0 ? null : _jsx(Fragment, { children: out });
}
export function Dialog({ open, onOpenChange, title, accessibleTitle, description, children, trigger, size = 'md', position = 'center', side = 'end', className, onOpenAutoFocus, }) {
    // Do not pass custom ids to Dialog.Title or aria-labelledby to Dialog.Content.
    // Radix manages titleId internally via context; overriding it breaks the
    // TitleWarning check (it looks for context.titleId in the DOM, not our id).
    return (_jsxs(RadixDialog.Root, { open: open, onOpenChange: onOpenChange, children: [trigger && _jsx(RadixDialog.Trigger, { asChild: true, children: trigger }), _jsxs(RadixDialog.Portal, { children: [_jsx(RadixDialog.Overlay, { className: styles.overlay }), _jsxs(RadixDialog.Content, { className: `${styles.content} ${styles[`size-${size}`]}${className ? ` ${className}` : ''}`, "data-position": position, "data-side": position === 'side' ? side : undefined, onOpenAutoFocus: onOpenAutoFocus, children: [title !== undefined || description !== undefined ? (_jsxs("header", { className: styles.header, children: [_jsxs("div", { className: styles['header-row'], children: [title !== undefined ? (_jsx(RadixDialog.Title, { className: styles.title, children: title })) : (_jsx("span", {})), _jsx(RadixDialog.Close, { className: styles.close, "aria-label": "Close", type: "button", children: _jsx(X, { "aria-hidden": "true", focusable: "false" }) })] }), description !== undefined ? (_jsx(RadixDialog.Description, { className: styles.description, children: description })) : (_jsx(VisuallyHidden, { children: _jsx(RadixDialog.Description, {}) }))] })) : accessibleTitle !== undefined ? (_jsxs(VisuallyHidden, { children: [_jsx(RadixDialog.Title, { children: accessibleTitle }), _jsx(RadixDialog.Description, {})] })) : (_jsx(VisuallyHidden, { children: _jsx(RadixDialog.Description, {}) })), intoBands(children)] })] })] }));
}
//# sourceMappingURL=Dialog.js.map